import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { PracticeChallenge } from "@/components/PracticeChallenge";
import { Insight } from "@/components/Insight";
import { Keyboard } from "@/components/Keyboard";
import {
  Tip, KeyIdea, Pull, Steps, Step, NextUp, KeyHighlights, Highlight,
} from "@/components/Callouts";

export default function PianoFoundation6() {
  const { lesson, course, accent } = findLesson("piano-foundation", "6");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyHighlights>
        <Highlight>Same five-finger position as Lesson 5: <strong>thumb on C, index on D, middle on E, ring on F, pinky on G</strong>. Three new drills built on top.</Highlight>
        <Highlight>Drill 1 — <strong>Thirds</strong>: skip a note each time. C → E → D → F → E → G. Fingering is <em>thumb-middle, index-ring, middle-pinky</em>.</Highlight>
        <Highlight>Drill 2 — <strong>Triplet pairs</strong>: walk through overlapping three-note groups. C-D-E, D-E-F, E-F-G — and back down. Add an <em>accent on the first note</em> for muscle strength.</Highlight>
        <Highlight>Drill 3 — <strong>Together-notes</strong>: press two notes at once (C+E, D+F, E+G). Trains the brain to lift two specific fingers and let the others rest.</Highlight>
        <Highlight>All three drills can be practised <strong>on a flat surface</strong> — your thigh, a book, the dining table. The piano isn't required to grow as a pianist.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="Drill 1 · Thirds" title="Skip a note — alternate fingers" accent={accent.hex}>
        <p>
          The first new drill is <strong>thirds</strong> — you skip the note in between each
          time. From C, the third is E (skip D). From D, the third is F. From E, the third
          is G. Same five-finger hand position; you just play the alternates.
        </p>

        <p className="font-mono text-[14px] text-amber-300">
          Up:  C-E  ·  D-F  ·  E-G<br />
          Down: G-E · F-D · E-C
        </p>

        <Tip>
          On C-E, the fingering is <strong>thumb → middle</strong> (not index!). On D-F it's
          index → ring. On E-G it's middle → pinky. Each pair skips a finger because the
          interval skips a note. The wrong instinct is to use adjacent fingers — fight it.
        </Tip>

        <Keyboard
          startOctave={3}
          octaves={2}
          highlights={[
            { pitch: "C4", color: "#22c55e", label: "1" },
            { pitch: "E4", color: "#22c55e", label: "3" },
            { pitch: "D4", color: "#3b82f6", label: "2" },
            { pitch: "F4", color: "#3b82f6", label: "4" },
            { pitch: "G4", color: "#a855f7", label: "5" },
          ]}
          height={170}
        />

        <PracticeChallenge
          courseSlug="piano-foundation"
          order="6"
          id="thirds"
          mode="sequence"
          title="Drill 1 · Thirds"
          prompt="Your turn — play the thirds: C – E – D – F – E – G. Skip a note each time. The glowing key is your next note."
          notes={["C4", "E4", "D4", "F4", "E4", "G4"]}
          startOctave={3}
          octaves={2}
        />

        <KeyIdea>
          Sing the swaras as you go. Sa–Ga (C–E), Re–Ma (D–F), Ga–Pa (E–G). The "skip" is
          easier to feel when you're hearing the interval as well as playing it.
        </KeyIdea>
      </Insight>

      <Insight eyebrow="Drill 2 · Triplet pairs" title="Overlapping three-note groups, with accents" accent={accent.hex}>
        <p>
          Three-note groups that share notes between them — we call them{" "}
          <strong>triplet pairs</strong>. The thumb stays glued to C; the index stays on D;
          everything stays in position — there's no crossing, no shifting.
        </p>

        <p className="font-mono text-[14px] text-amber-300">
          Up:   C-D-E  ·  D-E-F  ·  E-F-G<br />
          Down: G-F-E ·  F-E-D ·  E-D-C
        </p>

        <p>
          Once the pattern is clean, add an <strong>accent</strong> on the first of every
          three: <em>LOUD-soft-soft, LOUD-soft-soft</em>. The accent strengthens whichever
          finger plays the loud note — and makes the drill sound musical.
        </p>

        <Pull>
          Loud, soft, soft, loud, soft, soft. This is going to strengthen whichever
          finger has to press loud, isn't it? And it's also sounding very musical. So
          now, if you can't get that loud note to be loud, then make the other one
          softer.
        </Pull>
      </Insight>

      <Insight eyebrow="Drill 3 · Together-notes" title="Two fingers down at once" accent={accent.hex}>
        <p>
          The last drill in this lesson asks you to press two notes <em>simultaneously</em>{" "}
          — same intervals as the thirds drill, but landing as one event:
        </p>

        <p className="font-mono text-[14px] text-amber-300">
          Up:   C+E  ·  D+F  ·  E+G<br />
          Loop: D+F  ·  E+G  ·  D+F  ·  C+E
        </p>

        <p>
          This is harder than it looks. Your brain has to fire two specific fingers at the
          same instant while telling the others to stay relaxed. Slow it down all the way
          if it's clumsy.
        </p>

        <Tip>
          You don't need a piano for this exercise. Lay your hand on your thigh, on a book,
          on the dining table — the lift-and-press of two fingers at once is the same
          training whether or not a key is under them. you can grow as a pianist <em>literally anywhere</em>.
        </Tip>
      </Insight>

      <Insight eyebrow="Major to minor" title="Same three drills, with E♭" accent={accent.hex}>
        <p>
          Every drill above flips to minor with one tweak: drop the third (E) down to{" "}
          <strong>E♭</strong>. Thirds become C–E♭, D–F, E♭–G. Triplet pairs become C-D-E♭,
          D-E♭-F, E♭-F-G. Together-notes become C+E♭, D+F, E♭+G.
        </p>

        <Keyboard
          startOctave={3}
          octaves={2}
          highlights={[
            { pitch: "C4", color: "#3b82f6", label: "1" },
            { pitch: "D4", color: "#3b82f6", label: "2" },
            { pitch: "Eb4", color: "#ef4444", label: "♭3" },
            { pitch: "F4", color: "#3b82f6", label: "4" },
            { pitch: "G4", color: "#3b82f6", label: "5" },
          ]}
          height={170}
        />
      </Insight>

      <Insight eyebrow="The full session" title="What good practice looks like for these three" accent={accent.hex}>
        <Steps>
          <Step>
            Start with the simple up-and-down five-finger drill from Lesson 5 as a warm-up.
            One minute.
          </Step>
          <Step>
            Drill 1 — thirds. Major version, both hands. Then minor. Sing the swaras.
          </Step>
          <Step>
            Drill 2 — triplet pairs without accents, then with accents on the first of every
            three. Both hands. Major and minor.
          </Step>
          <Step>
            Drill 3 — together-notes. Slowly. Both hands. Pay attention to which fingers
            <em> aren't</em> meant to move.
          </Step>
          <Step>
            Switch to your thigh or a tabletop. Repeat all three drills there for a minute.
            You'll feel which fingers are still struggling — those are the ones to drill
            longest tomorrow.
          </Step>
        </Steps>

        <Tip>
          The whole point of practising "on a flat surface" away from the piano is finger
          independence. Lifting two fingers cleanly while the others stay still is a
          coordination skill — the piano just confirms what your hand already knows.
        </Tip>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-7"
        label="Lesson 7 · Posture"
        why="Now that the fingers are working harder, the body around them needs to support them properly. Posture is the next foundation."
      />
    </LessonShell>
  );
}
